import { TweenMax } from 'gsap';
import axios from 'axios';
import qs from 'qs';
import moment from 'moment';

import * as errors from '@/errors';

export const scrollToTop = (elem) => {
  if (process.browser) {
    TweenMax.to(elem || window, .3, {
      scrollTo: 0
    });
  }
};

export const processRequest = (ep, config, showLoader = true) => {
  const { method, url, baseURL } = ep;

  return axios({
    headers: { 'Content-Type': 'application/json' },
    baseURL,
    method,
    url,
    ...config,
    showLoader,
    paramsSerializer(params) {
      return qs.stringify(params, { arrayFormat: 'repeat', skipNulls: true });
    }
  });
};

export const processSuccessResponse = (res = {}) => {
  const data = res?.data?.data;
  const { InvalidResponse } = errors;

  if (!data) {
    throw new InvalidResponse();
  }

  return data;
};

export const processErrorResponse = (res = {}) => {
  const error = res?.data?.error;
  const { InvalidResponse, UnknownError } = errors;

  if (!error) {
    throw new InvalidResponse();
  }

  const { code, message } = error;

  Object.keys(errors).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(errors, key)) {
      if (code === errors[key].code) {
        throw new errors[key](message);
      }
    }
  });

  throw new UnknownError(message || 'Unknown Error');
};

export const isNumber = (value) => /^-?\d+(\.\d+)?$/.test(value);

export const isMoney = (value) => /^-?\d+(\.[0-9]{1,3})?$/.test(value);

export const isValidPhoneNumber = (value) => /^\+?\d{8,11}$/.test(value);

export const addCommas = (str_, comma_) => {
  if (!str_) return 0;
  let str = str_.toString();

  while (str[0] === '0') str = str.substring(1, str.length);

  const comma = comma_ || ',';

  let s = str;
  let tail = '';

  const dot = str.indexOf(comma === ',' ? '.' : ',');

  if ((dot >= 0) && (dot !== str.length - 1)) {
    tail = str.substring(dot + 1, str.length);

    if (comma === ',') {
      tail = `.${tail}`;
    } else {
      tail = `,${tail}`;
    }

    s = str.substring(0, dot);
  }

  let signed = false;
  if (s[0] === '-') {
    signed = true;
  }

  s = s.replace(/\D/g, '');

  if (s.length < 4) return `${s}${tail}`;

  let temp = '';
  let n = 0;

  for (let i = s.length - 1; i >= 0; i -= 1) {
    temp = `${s[i]}${temp}`;
    n += 1;
    if ((n % 3 === 0) && (i > 0)) temp = `${comma}${temp}`;
  }
  return `${signed ? '-' : ''}${temp}${tail}`;
};

export const removeCommas = (str_, comma_) => {
  if (!str_) return 0;
  let str = str_.toString();

  const comma = comma_ || ',';

  str = str.replaceAll(comma, '');
  if (comma === '.') {
    str = str.replace(',', '.');
  }

  return parseFloat(str, 10);
};

export const upperSnake = (str_) => {
  if (!str_) return '';
  let str = str_.toString();
  str = str.toUpperCase();
  str = str.replace(/\s+/g, '_');

  return str;
};

export const camelToUpperSnake = (str) => str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`).toUpperCase();

export const normalizeText = (str) => {
  let text = str;

  text = text.replace(/[ẵẳẫẩắằăặầấậạáàâãªäả]/g, 'a');
  text = text.replace(/[ẴẲẪẨẮẰĂẶẤẦÂẬẠÁÀÂÃÄẢ]/g, 'A');
  text = text.replace(/[ĨỈỊÍÌÎÏ]/g, 'I');
  text = text.replace(/[ĩỉịíìîï]/g, 'i');
  text = text.replace(/[ỸỶỴÝỲ]/g, 'Y');
  text = text.replace(/[ỹỷỵýỳ]/g, 'y');
  text = text.replace(/[ễểếềêệẹéèêëẻẽ]/g, 'e');
  text = text.replace(/[ỄỂẾỀÊỆẸÉÈÊËẺẺ]/g, 'E');
  text = text.replace(/[ỗổốồôộọóòôốồõºöỏơớờởợỡ]/g, 'o');
  text = text.replace(/[ỖỔỐỒÔỘỌÓÒÔỐỒÕÖỎƠỚỜỞ]/g, 'O');
  text = text.replace(/[ưữửựừứũủụúùûü]/g, 'u');
  text = text.replace(/[ƯỮỬỰỪỨŨỦỤÚÙÛÜ]/g, 'U');
  text = text.replace(/[’‘‹›‚]/g, '\'');
  text = text.replace(/[“”«»„]/g, '"');
  text = text.replace(/–/g, '-');
  text = text.replace(/ç/g, 'c');
  text = text.replace(/Ç/g, 'C');
  text = text.replace(/ñ/g, 'n');
  text = text.replace(/Ñ/g, 'N');
  text = text.replace(/đ/g, 'd');
  text = text.replace(/Đ/g, 'D');
  text = text.replace(/\s+/g, ' ');
  text = text.replace(/[^a-zA-Z0-9 ]+/g, ' ');

  text = text.toLowerCase();

  return text;
};

export const applyParams = (...args) => {
  let route = args[0] || '';

  let i = 1;
  while (i < args.length) {
    route = route.replace(/:[a-zA-Z-_]+\??/, args[i]);
    i += 1;
  }

  return route;
};

export const changeDigitToViText = (value_, shortened = true) => {
  let money;
  const value = removeCommas(value_);

  if (shortened) {
    money = Math.round(parseFloat(value) * 1000 * 1000);
  } else {
    money = parseInt(value, 10);
  }

  const billionDigit = Math.floor(money / (1000 * 1000 * 1000));
  money %= (1000 * 1000 * 1000);

  const millionDigit = Math.floor(money / (1000 * 1000));
  money %= (1000 * 1000);

  const hundredDigit = Math.floor(money / (100 * 1000));
  money %= (100 * 1000);

  const thousandDigit = Math.floor(money / 1000);
  money %= 1000;

  let result = '';

  if (billionDigit > 0) {
    result += `${billionDigit} tỷ `;
  }

  if (millionDigit > 0) {
    result += `${millionDigit} triệu `;
  }

  if (hundredDigit > 0) {
    result += `${hundredDigit} trăm `;
  }

  if (thousandDigit > 0) {
    result += `${thousandDigit} `;
  }

  if (hundredDigit > 0 || thousandDigit > 0) {
    result += 'nghìn ';
  }

  if (money > 0) {
    result += `${money} `;
  }

  if (result) {
    result += 'đồng';
  }
  return result;
};

export const formatDate = (date) => {
  const formatedDate = moment(date, 'YYYY-MM-DD');
  if (formatedDate.isValid()) {
    return formatedDate.format('D/M/YYYY');
  }
  return '';
};

export default {};
